import {useState, useRef, useEffect, useCallback} from "react";
import {Box, Stack, Typography, InputBase, Select, MenuItem, Button, IconButton, Chip} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {FilterIcon, RotateClockwiseIcon, Search01Icon, ZoomInIcon, ZoomOutIcon} from "@hugeicons/core-free-icons";
import {KnowledgeManagerRepository} from "../../../../repositories/KnowledgeManagerRepository";
import {Panel} from "../../dashboard/components/Panel";

const controlSx = {height: 40, display: "flex", alignItems: "center", gap: 1, px: 1.25, borderRadius: "10px", bgcolor: "#FAFAFD", color: "#6B6B80", fontSize: 13};
const selectSx = {borderRadius: "10px", bgcolor: "#FAFAFD", fontSize: 13, "& .MuiOutlinedInput-notchedOutline": {border: "none"}};

function buildGraphNodes(topic, label, rawNodes) {
  const typeColors = {entity: "#8491F7", relation: "#D9A13A", chunk: "#2F9A6A", topic: topic.color || "#8491F7"};

  const topicNode = {
    id: "__graph-center__", label, type: "topic",
    properties: {topicId: topic.id, topicName: topic.name, description: topic.description},
    x: 400, y: 300, vx: 0, vy: 0, color: topic.color || "#8491F7", size: 16,
  };

  const nodes = rawNodes.map((node, index) => {
    const angle = rawNodes.length > 0 ? (index / rawNodes.length) * Math.PI * 2 : 0;
    const radius = 120 + Math.random() * 80;
    return {
      id: node.id, label: node.label, type: node.type, properties: node.properties,
      x: 400 + Math.cos(angle) * radius + (Math.random() - 0.5) * 40,
      y: 300 + Math.sin(angle) * radius + (Math.random() - 0.5) * 40,
      vx: 0, vy: 0, color: typeColors[node.type] ?? "#8491F7", size: 6 + Math.random() * 6,
    };
  });

  return [topicNode, ...nodes];
}

export const GraphTab = ({topic}) => {
  const canvasRef = useRef(null);
  const [graph, setGraph] = useState({nodes: [], edges: []});
  const [isLoading, setIsLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({x: 0, y: 0});
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeSearchTerm, setNodeSearchTerm] = useState("");
  const [labelQuery, setLabelQuery] = useState("*");
  const [activeLabel, setActiveLabel] = useState("");
  const [labelSuggestions, setLabelSuggestions] = useState([]);
  const [popularLabels, setPopularLabels] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [hoveredNode, setHoveredNode] = useState(null);
  const [error, setError] = useState(null);
  const [maxDepth, setMaxDepth] = useState("2");
  const [maxNodes, setMaxNodes] = useState("100");
  const isDragging = useRef(false);
  const dragStart = useRef({x: 0, y: 0});
  const dragNode = useRef(null);

  const getNodeAt = useCallback((mx, my) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = (mx - rect.left - offset.x) / zoom;
    const y = (my - rect.top - offset.y) / zoom;
    for (const node of graph.nodes) {
      const dx = node.x - x;
      const dy = node.y - y;
      if (Math.sqrt(dx * dx + dy * dy) < node.size + 4) return node;
    }
    return null;
  }, [graph.nodes, zoom, offset]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.clientWidth * dpr;
    canvas.height = canvas.clientHeight * dpr;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
    ctx.save();
    ctx.translate(offset.x, offset.y);
    ctx.scale(zoom, zoom);

    const filteredNodes = filterType === "all" ? graph.nodes : graph.nodes.filter(node => node.type === filterType);
    const filteredIds = new Set(filteredNodes.map(node => node.id));

    for (const edge of graph.edges) {
      if (!filteredIds.has(edge.source) || !filteredIds.has(edge.target)) continue;
      const source = graph.nodes.find(node => node.id === edge.source);
      const target = graph.nodes.find(node => node.id === edge.target);
      if (!source || !target) continue;

      ctx.beginPath();
      ctx.moveTo(source.x, source.y);
      ctx.lineTo(target.x, target.y);
      ctx.strokeStyle = selectedNode && (selectedNode.id === source.id || selectedNode.id === target.id)
        ? "rgba(132,145,247,0.6)" : "rgba(35,38,77,0.08)";
      ctx.lineWidth = selectedNode && (selectedNode.id === source.id || selectedNode.id === target.id) ? 1.5 : 0.5;
      ctx.stroke();

      if (zoom > 0.8) {
        const midX = (source.x + target.x) / 2;
        const midY = (source.y + target.y) / 2;
        ctx.fillStyle = "rgba(35,38,77,0.35)";
        ctx.font = "8px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(edge.label, midX, midY - 3);
      }
    }

    for (const node of filteredNodes) {
      const isHighlighted = nodeSearchTerm.length > 0 && node.label.toLowerCase().includes(nodeSearchTerm.toLowerCase());
      const isSelected = selectedNode?.id === node.id;
      const isHovered = hoveredNode?.id === node.id;

      ctx.beginPath();
      ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2);

      if (isSelected || isHighlighted) {
        ctx.shadowColor = node.color;
        ctx.shadowBlur = 12;
      }

      ctx.fillStyle = isHovered ? node.color : nodeSearchTerm && !isHighlighted ? `${node.color}40` : node.color;
      ctx.fill();
      ctx.shadowBlur = 0;

      if (isSelected) {
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      if (zoom > 0.6 || isSelected || isHighlighted || isHovered || node.type === "topic") {
        ctx.fillStyle = nodeSearchTerm && !isHighlighted ? "rgba(35,38,77,0.25)" : "rgba(35,38,77,0.75)";
        ctx.font = `${node.type === "topic" ? "bold 11px" : "10px"} sans-serif`;
        ctx.textAlign = "center";
        ctx.fillText(node.label, node.x, node.y + node.size + 12);
      }
    }

    ctx.restore();
  }, [filterType, graph, hoveredNode, nodeSearchTerm, offset, selectedNode, zoom]);

  const resetView = useCallback(() => {
    setZoom(1);
    setOffset({x: 0, y: 0});
    setSelectedNode(null);
    setNodeSearchTerm("");
    setFilterType("all");
  }, []);

  const loadGraph = useCallback(async (label) => {
    const nextLabel = label.trim();
    if (!nextLabel) {
      setError("Choose a graph label first.");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await KnowledgeManagerRepository.fetchGraph(topic.id, {
        label: nextLabel, maxDepth: Number(maxDepth), maxNodes: Number(maxNodes),
      });
      const nodes = buildGraphNodes(topic, nextLabel, response.nodes);
      const nodeIds = new Set(nodes.map(node => node.id));
      const edges = response.edges
        .filter(edge => nodeIds.has(edge.source) && nodeIds.has(edge.target))
        .map(edge => ({source: edge.source, target: edge.target, label: edge.label}));

      setActiveLabel(nextLabel);
      setGraph({nodes, edges});
      setSelectedNode(nodes[0] ?? null);
      setOffset({x: 0, y: 0});
      setZoom(1);
    } catch (err) {
      setGraph({nodes: [], edges: []});
      setSelectedNode(null);
      setError(err.response?.data?.message || "Knowledge graph operation failed");
    } finally {
      setIsLoading(false);
    }
  }, [maxDepth, maxNodes, topic]);

  useEffect(() => {draw();}, [draw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const observer = new ResizeObserver(() => draw());
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [draw]);

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      setError(null);
      try {
        const popular = await KnowledgeManagerRepository.listPopularGraphLabels(topic.id, 12);
        if (cancelled) return;
        setPopularLabels(popular);
        setLabelSuggestions(popular);
      } catch (err) {
        if (cancelled) return;
        setPopularLabels([]);
        setLabelSuggestions([]);
      }
      if (!cancelled) void loadGraph("*");
    }
    void bootstrap();
    return () => {cancelled = true;};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic.id]);

  useEffect(() => {
    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      const q = labelQuery.trim();
      if (!q) {
        setLabelSuggestions(popularLabels);
        return;
      }
      KnowledgeManagerRepository.searchGraphLabels(topic.id, q, 10)
        .then(labels => {if (!cancelled) setLabelSuggestions(labels);})
        .catch(() => {if (!cancelled) setLabelSuggestions([]);});
    }, 250);
    return () => {cancelled = true; window.clearTimeout(timeoutId);};
  }, [labelQuery, popularLabels, topic.id]);

  const handleMouseDown = (e) => {
    const node = getNodeAt(e.clientX, e.clientY);
    if (node) {
      dragNode.current = node;
      setSelectedNode(node);
      return;
    }
    isDragging.current = true;
    dragStart.current = {x: e.clientX - offset.x, y: e.clientY - offset.y};
    setSelectedNode(null);
  };

  const handleMouseMove = (e) => {
    if (dragNode.current) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      dragNode.current.x = (e.clientX - rect.left - offset.x) / zoom;
      dragNode.current.y = (e.clientY - rect.top - offset.y) / zoom;
      draw();
      return;
    }
    if (isDragging.current) {
      setOffset({x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y});
      return;
    }
    const node = getNodeAt(e.clientX, e.clientY);
    setHoveredNode(node);
    const canvas = canvasRef.current;
    if (canvas) canvas.style.cursor = node ? "pointer" : "grab";
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    dragNode.current = null;
  };

  const handleDoubleClick = (e) => {
    const node = getNodeAt(e.clientX, e.clientY);
    if (!node) return;
    setSelectedNode(node);
    setLabelQuery(node.label);
    void loadGraph(node.label);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(currentZoom => Math.max(0.2, Math.min(3, currentZoom * delta)));
  };

  return (
    <Stack gap={2}>
      <Stack direction="row" gap={1.25} flexWrap="wrap">
        <Box sx={{...controlSx, flex: 1, minWidth: 220}}>
          <HugeiconsIcon icon={Search01Icon} size={14}/>
          <InputBase
            fullWidth value={labelQuery} onChange={(e) => setLabelQuery(e.target.value)}
            onKeyDown={(e) => {if (e.key === "Enter") {e.preventDefault(); void loadGraph(labelQuery);}}}
            placeholder="Search graph labels..." sx={{flex: 1, fontSize: 13, color: "#23264D"}}
          />
        </Box>
        <Select size="small" value={maxDepth} onChange={(e) => setMaxDepth(e.target.value)} sx={{...selectSx, width: 120}}>
          <MenuItem value="1">Depth 1</MenuItem>
          <MenuItem value="2">Depth 2</MenuItem>
          <MenuItem value="3">Depth 3</MenuItem>
        </Select>
        <Select size="small" value={maxNodes} onChange={(e) => setMaxNodes(e.target.value)} sx={{...selectSx, width: 130}}>
          <MenuItem value="50">50 nodes</MenuItem>
          <MenuItem value="100">100 nodes</MenuItem>
          <MenuItem value="200">200 nodes</MenuItem>
        </Select>
        <Button
          onClick={() => void loadGraph(labelQuery)}
          sx={{textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px', bgcolor: '#374151', color: '#fff', '&:hover': {bgcolor: '#1F2937'}}}
        >
          Load Graph
        </Button>
      </Stack>

      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
        {activeLabel && (
          <Chip label={`Active: ${activeLabel}`} size="small" sx={{fontSize: 11, bgcolor: 'rgba(132, 145, 247, 0.12)', color: '#4B55C9'}}/>
        )}
        <Button
          size="small"
          onClick={() => {setLabelQuery("*"); void loadGraph("*");}}
          sx={activeLabel === "*"
            ? {textTransform: 'none', fontSize: 11, borderRadius: '10px', bgcolor: '#374151', color: '#fff', '&:hover': {bgcolor: '#1F2937'}}
            : {textTransform: 'none', fontSize: 11, borderRadius: '10px', bgcolor: '#FAFAFD', color: '#6B6B80', '&:hover': {bgcolor: '#ECECF3'}}}
        >
          All (*)
        </Button>
        {labelSuggestions.slice(0, 8).map(label => (
          <Button
            key={label} size="small"
            onClick={() => {setLabelQuery(label); void loadGraph(label);}}
            sx={label === activeLabel
              ? {textTransform: 'none', fontSize: 11, borderRadius: '10px', bgcolor: '#374151', color: '#fff', '&:hover': {bgcolor: '#1F2937'}}
              : {textTransform: 'none', fontSize: 11, borderRadius: '10px', bgcolor: '#FAFAFD', color: '#6B6B80', '&:hover': {bgcolor: '#ECECF3'}}}
          >
            {label}
          </Button>
        ))}
      </Stack>

      <Stack direction="row" gap={1.25} flexWrap="wrap" alignItems="center">
        <Box sx={{...controlSx, flex: 1, minWidth: 200}}>
          <HugeiconsIcon icon={Search01Icon} size={14}/>
          <InputBase
            fullWidth value={nodeSearchTerm} onChange={(e) => setNodeSearchTerm(e.target.value)}
            placeholder="Filter visible nodes..." sx={{flex: 1, fontSize: 13, color: "#23264D"}}
          />
        </Box>
        <Select
          size="small" value={filterType} onChange={(e) => setFilterType(e.target.value)} sx={{...selectSx, width: 140}}
          renderValue={(v) => (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <HugeiconsIcon icon={FilterIcon} size={13}/> {v === "all" ? "All Types" : v}
            </Stack>
          )}
        >
          <MenuItem value="all">All Types</MenuItem>
          <MenuItem value="entity">Entities</MenuItem>
          <MenuItem value="relation">Relations</MenuItem>
          <MenuItem value="chunk">Chunks</MenuItem>
          <MenuItem value="topic">Topic</MenuItem>
        </Select>
        <Stack direction="row" gap={0.5}>
          <IconButton size="small" sx={{borderRadius: "10px", bgcolor: "#FAFAFD"}} onClick={() => setZoom(z => Math.min(3, z * 1.2))}>
            <HugeiconsIcon icon={ZoomInIcon} size={16}/>
          </IconButton>
          <IconButton size="small" sx={{borderRadius: "10px", bgcolor: "#FAFAFD"}} onClick={() => setZoom(z => Math.max(0.2, z * 0.8))}>
            <HugeiconsIcon icon={ZoomOutIcon} size={16}/>
          </IconButton>
          <IconButton size="small" sx={{borderRadius: "10px", bgcolor: "#FAFAFD"}} onClick={resetView}>
            <HugeiconsIcon icon={RotateClockwiseIcon} size={16}/>
          </IconButton>
        </Stack>
      </Stack>

      {error && (
        <Box sx={{borderRadius: "12px", bgcolor: "rgba(220, 38, 38, 0.08)", p: 1.5}}>
          <Typography sx={{fontSize: 12, color: "#DC2626"}}>{error}</Typography>
        </Box>
      )}

      <Panel sx={{overflow: "hidden", p: 0}}>
        <Box sx={{position: "relative"}}>
          {isLoading && (
            <Box sx={{
              position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.85)", zIndex: 10,
            }}>
              <Box sx={{textAlign: "center"}}>
                <Box sx={{
                  width: 24, height: 24, border: "2px solid #8491F7", borderTopColor: "transparent", borderRadius: "50%",
                  mx: "auto", mb: 1, animation: "spin 0.8s linear infinite", "@keyframes spin": {to: {transform: "rotate(360deg)"}},
                }}/>
                <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>Fetching knowledge graph...</Typography>
              </Box>
            </Box>
          )}
          <Box
            component="canvas" ref={canvasRef}
            sx={{width: "100%", height: 450, background: "linear-gradient(to bottom right, #FAFAFD, #fff)"}}
            onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp} onDoubleClick={handleDoubleClick} onWheel={handleWheel}
          />
          <Box sx={{position: "absolute", bottom: 12, left: 12, bgcolor: "rgba(255,255,255,0.92)", borderRadius: "10px", p: 1, fontSize: 10}}>
            <Typography sx={{fontSize: 10, color: "#8A8AA0", pb: 0.5, mb: 0.5, borderBottom: "1px solid #ECECF3"}}>
              Double-click a node to refocus
            </Typography>
            <Stack gap={0.5}>
              {[["Focus Label", topic.color || "#8491F7"], ["Entity", "#8491F7"], ["Relation", "#D9A13A"], ["Chunk", "#2F9A6A"]].map(([label, color]) => (
                <Stack key={label} direction="row" alignItems="center" gap={0.75}>
                  <Box sx={{width: 10, height: 10, borderRadius: "50%", bgcolor: color}}/>
                  <Typography sx={{fontSize: 10, color: "#23264D"}}>{label}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
          <Box sx={{position: "absolute", bottom: 12, right: 12, bgcolor: "rgba(255,255,255,0.92)", borderRadius: "10px", px: 1, py: 0.5}}>
            <Typography sx={{fontSize: 10, color: "#8A8AA0"}}>{Math.round(zoom * 100)}%</Typography>
          </Box>
        </Box>
      </Panel>

      {selectedNode && (
        <Panel sx={{p: 2}}>
          <Stack direction="row" alignItems="center" gap={1} sx={{mb: 1}}>
            <Box sx={{width: 12, height: 12, borderRadius: "50%", bgcolor: selectedNode.color}}/>
            <Typography sx={{fontSize: 14, color: "#23264D"}}>{selectedNode.label}</Typography>
            <Chip label={selectedNode.type} size="small" sx={{fontSize: 10, bgcolor: "#F2F2F7", color: "#6B6B80"}}/>
          </Stack>
          <Stack gap={0.5}>
            <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
              Connections: {graph.edges.filter(e => e.source === selectedNode.id || e.target === selectedNode.id).length}
            </Typography>
            <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
              Connected to: {graph.edges
              .filter(e => e.source === selectedNode.id || e.target === selectedNode.id)
              .map(e => {
                const otherId = e.source === selectedNode.id ? e.target : e.source;
                return graph.nodes.find(n => n.id === otherId)?.label;
              })
              .filter(Boolean)
              .join(", ")}
            </Typography>
          </Stack>
          {selectedNode.properties && Object.keys(selectedNode.properties).length > 0 && (
            <Box sx={{mt: 1.5, pt: 1.5, borderTop: "1px solid #ECECF3"}}>
              <Typography sx={{fontSize: 12, color: "#23264D", mb: 1}}>Attributes</Typography>
              <Stack gap={1}>
                {Object.entries(selectedNode.properties).map(([key, value]) => (
                  <Stack key={key} direction="row" gap={1.5}>
                    <Typography sx={{fontSize: 12, color: "#8A8AA0", width: 120, wordBreak: "break-all"}}>{key}</Typography>
                    <Typography sx={{fontSize: 12, color: "#23264D", wordBreak: "break-word"}}>{String(value)}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
        </Panel>
      )}

      <Stack direction="row" gap={1.5}>
        {[
          {value: graph.nodes.length, label: "Nodes"},
          {value: graph.edges.length, label: "Edges"},
          {value: popularLabels.length, label: "Popular Labels"},
        ].map(stat => (
          <Box key={stat.label} sx={{flex: 1, bgcolor: "#FAFAFD", borderRadius: "12px", p: 1.5, textAlign: "center"}}>
            <Typography sx={{fontSize: 20, color: "#23264D", fontWeight: 700}}>{stat.value}</Typography>
            <Typography sx={{fontSize: 11, color: "#8A8AA0"}}>{stat.label}</Typography>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
};
